import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ClipboardCheck, ArrowLeft, CheckCircle2, Pencil, Archive, ArrowRight } from "lucide-react";
import { C, StatusBadge, AudienceChips, fmtDate } from "./sopsUi";

export default function SopReview() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setItems(null);
    try {
      const [d1, d2] = await Promise.all([
        api.get("/sops", { params: { status: "pending_review" } }),
        api.get("/sops", { params: { status: "draft" } }),
      ]);
      const merged = [...(d1.data.items || []), ...(d2.data.items || [])];
      setItems(merged.filter((s) => s.can_manage));
    } catch (e) { toast.error(errMsg(e)); setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, verb) => {
    setBusy(id);
    try { await api.post(`/sops/${id}/${verb}`); toast.success("Done."); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  return (
    <div className="max-w-4xl mx-auto pb-16" data-testid="sop-review-page">
      <button onClick={() => navigate("/sops")} className="text-[13px] flex items-center gap-1 mb-3" style={{ color: C.slate }}>
        <ArrowLeft size={14} /> Back to SOP Library
      </button>
      <h1 className="font-display text-2xl font-bold flex items-center gap-2 mb-1" style={{ color: C.charcoal }}>
        <ClipboardCheck size={22} style={{ color: C.navy }} /> SOP Review Queue
      </h1>
      <p className="text-[13px] mb-5" style={{ color: C.slate }}>Drafts and SOPs awaiting review. Nothing is public until you publish it.</p>

      {items === null && <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>}
      {items && items.length === 0 && (
        <Card className="p-10 text-center border-[#E6EDF2]" style={{ color: C.slate }} data-testid="sop-review-empty">Nothing waiting for review. 🎉</Card>
      )}
      <div className="space-y-3">
        {items?.map((s) => (
          <Card key={s.id} className="p-4 border-[#E6EDF2]" data-testid={`sop-review-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <button onClick={() => navigate(`/sops/${s.id}`)} className="font-display font-semibold text-[15px] flex items-center gap-1" style={{ color: C.navy }}>
                  {s.title} <ArrowRight size={13} />
                </button>
                <p className="text-[13px] mt-0.5 line-clamp-2" style={{ color: C.charcoal }}>{s.summary}</p>
              </div>
              <StatusBadge status={s.status} />
            </div>
            <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(28,67,88,0.08)", color: C.navy }}>{s.category_label}</span>
                <AudienceChips audiences={s.audiences} />
                <span className="text-[11px]" style={{ color: C.slate }}>Updated {fmtDate(s.updated_at)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" onClick={() => navigate(`/sops/${s.id}/edit`)} data-testid={`sop-review-edit-${s.id}`} className="h-8 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Pencil size={13} /> Edit</Button>
                <Button size="sm" variant="outline" disabled={busy === s.id} onClick={() => act(s.id, "archive")} className="h-8 gap-1 border-[#D6E0E7]" style={{ color: C.slate }}><Archive size={13} /> Archive</Button>
                <Button size="sm" disabled={busy === s.id} onClick={() => act(s.id, "publish")} data-testid={`sop-review-publish-${s.id}`} className="h-8 gap-1 bg-[#2F7D5A] hover:bg-[#276849] text-white"><CheckCircle2 size={13} /> Publish</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
